// Libraries
import React, { useState, useContext } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Node } from 'slate';
import _ from 'lodash';
import {
  Box,
  Stack,
  TextField,
  Collapse,
  Autocomplete,
  Button,
  useTheme,
  IconButton,
  MenuItem
} from '@mui/material';
import SearchRounded from '@mui/icons-material/SearchRounded';
import ManageSearchOutlinedIcon from '@mui/icons-material/ManageSearchOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import DocumentationTooltip from '../../../../Common/DocumentationTooltip.js';
import HistoryIcon from '@mui/icons-material/History';


// Local
import {
  KEYWORD_SUGGESTIONS,
  KEYWORDS_BASED_ON_SEARCH,
  DOCUMENT_SEARCH,
  PAGINATED_SEARCH
} from '../../../Queries/nlp.js';
import { ADD_PAST_SEARCH } from '../../../../Queries';
import { DocumentContext } from '../../../DocumentContext';
import { generateSearchStats } from '../../../Utilities/generateSearchStats';
import { addScoresToSearchResults } from '../../../Utilities/addScoresToSearchResults.js';
import SearchKeywords from '../SearchKeywords';
import EditableChip from './EditableChip.js';
import KeywordFilteringList from './KeywordFiltering.js'
import GlobalModal from '../../../../Common/GlobalModal';
import PastSearches from './PastSearches.js';
import { isFilterValid, filterParser, encodeFilter } from './KeywordFilteringScripts'
import { notify } from '../../../../Common/notify'

/**
 * Controls our search field
 *
 * @param {object} client: The apollo client method
 */

const SearchBox = ({ heightRef, filters, setFilters, selectorOptions, selectedOption, setSelectedOption }) => {
  const theme = useTheme();
  const context = useContext(DocumentContext);

  const [modal, setModal] = useState(false);
  const [collapsed, collapsedToggle] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [autoCompleteInputValue, setAutoCompleteInputValue] = useState('');

  const [keywordSuggestions] = useLazyQuery(KEYWORD_SUGGESTIONS, {
    fetchPolicy: 'network-only',
    onCompleted: ({ keywordSuggestions }) => {
      const options = _.uniq(keywordSuggestions.response);

      setSuggestions(options);
    },
  });

  const [keywordsBasedOnSearch] = useLazyQuery(KEYWORDS_BASED_ON_SEARCH);

  const [paginatedSearch] = useLazyQuery(PAGINATED_SEARCH);

  const [addPastSearch] = useMutation(ADD_PAST_SEARCH, {
    fetchPolicy: 'network-only',
  });

  const [documentSearch] = useLazyQuery(DOCUMENT_SEARCH, {
    fetchPolicy: 'network-only',
    onCompleted: ({ documentSearch }) => {
      if (documentSearch.status === 'success') {
        context.updateContext('paperIds', documentSearch.response.paper_list);
        context.updateContext(
          'rerankingScores',
          documentSearch.response.reranking_scores
        );
        context.updateContext(
          'prefetchingScores',
          documentSearch.response.prefetching_scores
        );
        context.updateContext(
          'pageCount',
          documentSearch.response.paper_list.length / 10
        );

        context.updateContext('loadingRequest', false);
        context.updateContext(
          'loadingMessage',
          generateSearchStats(documentSearch.response.search_stats)
        );

        paginatedSearch({
          fetchPolicy: 'network-only',
          variables: {
            keywords: context.keywords,
            paper_list: documentSearch.response.paper_list.slice(0, 10),
          },
          onCompleted: ({ paginatedSearch }) => {
            const updatedSearchResults = addScoresToSearchResults(
              paginatedSearch.response, // in batches of 10 papers
              documentSearch.response.reranking_scores, // scores for all papers
              documentSearch.response.prefetching_scores, // prefetching scores for all papers
              1, // current page
              10 // items per page
            );
            context.updateContext('searchResults', updatedSearchResults);
            context.updateContext('currentPage', 1);

            // Toggle away from personal libraries
            context.updateContext('searchLibrary', false);
            context.updateContext('onlyManuscriptLibrary', false);
          },
        });

        keywordsBasedOnSearch({
          fetchPolicy: 'network-only',
          variables: {
            ranking_variable: context.searchTerm,
            keywords: context.keywords,
            paper_list: documentSearch.response.paper_list,
          },
          onCompleted: ({ keywordsBasedOnSearch }) => {
            context.updateContext(
              'keywordsBasedOnSearch',
              keywordsBasedOnSearch.response
            );
          },
        });
      } else {
        context.updateContext('loadingRequest', false);
        context.updateContext('loadingMessage', 'Paper retrieval failed');
      }
    },
  });

  const handleInputChange = async (query) => {
    setAutoCompleteInputValue(query);

    if (!query) {
      setSuggestions([]);
      return;
    }

    keywordSuggestions({
      variables: {
        keyword: query,
      },
    });
  };

  const extractMetadata = (bookmarks) =>
    bookmarks.map((bookmark) => {
      return {
        collection: bookmark.id_collection,
        id_field: bookmark.id_field,
        id_type: bookmark.id_type,
        id_value: bookmark.id_value,
      };
    });

  const onSubmit = (e) => {
    e.preventDefault();

    let rankingVariable = '';
    let paperList = undefined;

    // If quicksearch was clicked, then the field contains this text
    // we search using the manuscript
    if (context.searchTerm === '[Contents of manuscript]') {
      rankingVariable = context.value.map((n) => Node.string(n)).join('\n');
    } else {
      rankingVariable = context.searchTerm;
    }
    
    // if empty ranking variable and keywords do not search
    if (!rankingVariable && !context.keywords.length) {
      notify("Please provide search text or keywords")
    } else {

      let rankingCollection = selectedOption;

      // If Library is selected, then only use the paper ids tied to the note
      if (selectedOption == selectorOptions[1].value) {
        paperList = extractMetadata(
          context.bookmarks.filter((bookmark) => {
            return context.manuscriptLibrary.some(
              (libraryItem) => libraryItem._id === bookmark._id
            );
          })
        );
        rankingCollection = "";
      }
  
      context.updateContext('loadingRequest', true);
      context.updateContext('loadingMessage', 'Retrieving papers');
  
      // Clean Filters and keep the validated ones
      const cleanedFilters = filters.filter(isFilterValid);
      setFilters(cleanedFilters);
  
      // Parse filters and concatenate them as keywords for the document search engine
      const parsedFilters = cleanedFilters.map(filterParser).filter(f => f !== "");
      const keywordAndFilters = [...context.keywords, ...parsedFilters]
  
      documentSearch({
        variables: {
          ranking_variable: rankingVariable,
          keywords: keywordAndFilters,
          paper_list: paperList,
          rankingCollection: rankingCollection
        },
      });
  
      // add past search if at least there is some text or keywords
      if (keywordAndFilters.length !== 0 || context.searchTerm !== '' || cleanedFilters.length !== 0 ) {
        addPastSearch({
          variables: {
            doc_id: context.documentId,
            searchTerm: context.searchTerm,
            keywords: context.keywords,
            filters: cleanedFilters.map(encodeFilter)
          }
        })
      }
      
    }


  };

  const borderColor = theme.palette.mode === 'light' ? '#c4c4c5' : '#484c50';

  return (
    <>
    <GlobalModal
      isOpen={modal}
      setOpen={setModal}
      heading={'Searches History'}
      subheading={'Find your past searches here'}
      width={"800px"}
    >
      <PastSearches setModal={setModal} setFilters={setFilters} collapsedToggle={collapsedToggle}/>
    </GlobalModal>
    <Box
      ref={heightRef}
      component="form"
      noValidate
      autoComplete="off"
      onSubmit={onSubmit}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-start',
        maxWidth: '100%',
        padding: '10px 10px 0px 10px',
        borderBottom: (theme) =>
          theme.palette.mode === 'dark'
            ? '1px solid #333333'
            : '1px solid #d4d4d4',
      }}
    >
      <Stack
        direction="row"
        sx={{
          marginBottom: '10px',
          fontSize: '12px',
          width: '100%',
        }}
      >
        <DocumentationTooltip
          tooltipText={
            context.searchLibrary || context.onlyManuscriptLibrary
              ? 'Discover library papers relevant to your query.'
              : 'Discover papers relevant to your query.'
          }
          linkSuffix="help-centre/discover-the-literature#conduct-discovery-directly"
        >
          <Button
            type="submit"
            variant="outlined"
            sx={{
              borderTopRightRadius: '0px',
              borderBottomRightRadius: '0px',
              border: `1px solid ${borderColor}`,
              borderRight: 'none',
            }}
          >
            {context.searchLibrary || context.onlyManuscriptLibrary ? (
              <ManageSearchOutlinedIcon />
            ) : (
              <SearchRounded />
            )}
          </Button>
        </DocumentationTooltip>
        <TextField
          fullWidth
          name="search"
          label="Semantic search"
          placeholder="Semantic discovery - Your text will be used to rank the filtered papers"
          InputLabelProps={{ shrink: true }}
          id="fullWidth"
          data-tip="Text for retrieving and ranking papers"
          data-delay-show="1000"
          size="small"
          value={context.searchTerm}
          onChange={(event) => {
            context.updateContext('searchTerm', event.target.value);
          }}
          sx={{
            fontSize: '12px',
            width: '100%',
            '& .MuiOutlinedInput-root': {
              borderTopLeftRadius: '0px !important',
              borderBottomLeftRadius: '0px !important',
            },
          }}
        />
      </Stack>
      
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          width: '100%',
        }}
      >
        <TextField
          select
          id="selector"
          label="Select Collection"
          variant="outlined"
          value={selectedOption}
          onChange={(e) => setSelectedOption(e.target.value)}
          sx={{
            width: '30%',
            marginRight: '10px',
            marginBottom: '10px'
          }}
        >
          {selectorOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <Autocomplete
          fullWidth
          multiple
          id="tags-filled"
          options={suggestions}
          inputValue={autoCompleteInputValue}
          onInputChange={(_, newInputValue) => {
            handleInputChange(newInputValue);
          }}
          freeSolo
          sx={{
            width: '70%',
            marginBottom: '10px',
          }}
          value={context.keywords}
          onChange={(_, newValue) => {
            context.updateContext('keywords', newValue);
          }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <EditableChip
                key={option}
                option={option}
                index={index}
                getTagProps={getTagProps}
                setAutoCompleteInputValue={setAutoCompleteInputValue}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
            {...params}
            label="Content based filter"
            InputLabelProps={{ shrink: true }}
            placeholder="Constrain your search with keywords - Check advanced filters to refine your search"
            variant="outlined"
          />
          )}
        />
        <DocumentationTooltip
          tooltipText="View past searches"
        >
          <IconButton
            color="primary"
            aria-label="show past searches"
            component="label"
            onClick={() => setModal(true)}
            sx={{
              top: '-5px'
            }}
          >
            <HistoryIcon sx={{ color: 'grey' }} />
          </IconButton>
        </DocumentationTooltip>
        <DocumentationTooltip
          tooltipText="Advanced filters to refine your search"
        >
          <IconButton
            color="primary"
            aria-label="show advanced filters"
            component="label"
            onClick={() => {
              collapsedToggle(!collapsed);
              }}
            sx={{
              top: '-5px'
              }}
          >
            {collapsed ? (
              <TuneOutlinedIcon />
              ) : (
                <TuneOutlinedIcon sx={{ color: 'grey' }} />
                )}
          </IconButton>
        </DocumentationTooltip>
      </Stack>
      <SearchKeywords />
      <Collapse
        in={collapsed}
        timeout="auto"
        unmountOnExit
        sx={{
          width: '100%',
        }}
      >
        <KeywordFilteringList filters={filters} setFilters={setFilters} />
      </Collapse>
    </Box>
    </>
  );
};

export default SearchBox;
